import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { sampleQuizQuestions } from "../data/quizQuestions";

export default function QuizBattlesTab({ currentUser, followingList, followersList, showToast }) {
  const [battles, setBattles] = useState([]);
  const [loadingBattles, setLoadingBattles] = useState(true);
  const [selectedOpponent, setSelectedOpponent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Active playing state
  const [activeBattle, setActiveBattle] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState("");
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60); 
  const [isFinished, setIsFinished] = useState(false);

  const getEligibleOpponents = () => {
    const combined = [...(followersList || []), ...(followingList || [])];
    const uniqueMap = new Map();
    combined.forEach(user => {
      if (user && user.id && user.id !== currentUser?.uid) {
        uniqueMap.set(user.id, user);
      }
    });
    return Array.from(uniqueMap.values());
  };

  const eligibleOpponents = getEligibleOpponents();

  const getUserProfile = (userId) => {
    if (!userId) {
      return { id: "", username: "opponent", photoURL: "https://via.placeholder.com/150" };
    }
    if (userId === currentUser?.uid) {
      return {
        id: currentUser.uid,
        username: currentUser.reloadUserInfo?.screenName || currentUser.username || currentUser.displayName || currentUser.name || "you",
        photoURL: currentUser.photoURL || "https://via.placeholder.com/150"
      };
    }
    const found = eligibleOpponents.find(u => u.id === userId);
    if (found) {
      return {
        id: userId,
        username: found.username || found.name || found.displayName || "opponent",
        photoURL: found.photoURL || "https://via.placeholder.com/150"
      };
    }
    return { id: userId, username: "opponent", photoURL: "https://via.placeholder.com/150" };
  };

  const getOpponentDetails = (participants) => {
    if (!participants || !currentUser) return null;
    const opponentId = participants.find(id => id !== currentUser.uid);
    if (!opponentId) return null;
    return getUserProfile(opponentId);
  };

  // Real-Time Listener
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    setLoadingBattles(true);
    const q = query(collection(db, "battles"), where("participants", "array-contains", currentUser.uid));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const list = [];
      querySnapshot.forEach(docSnap => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });
      
      list.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
      setBattles(list);
      setLoadingBattles(false);
    }, (error) => {
      console.error("Error fetching quiz battles:", error);
      setLoadingBattles(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (activeBattle) {
      const liveBattle = battles.find(b => b.id === activeBattle.id);
      if (liveBattle && liveBattle.status !== activeBattle.status) {
        setActiveBattle(liveBattle);
      }
    }
  }, [battles, activeBattle]);

  // Timer effect
  useEffect(() => {
    if (!activeBattle || isFinished) return;
    if (timeLeft <= 0) {
      handleFinishBattle(score);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, activeBattle, isFinished]);

  const handleStartBattle = async (e) => {
    e.preventDefault();
    if (!selectedOpponent) {
      showToast("Please select a connected peer for the battle.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "battles"), {
        participants: [currentUser.uid, selectedOpponent],
        status: "pending",
        createdBy: currentUser.uid,
        scores: {},
        createdAt: serverTimestamp()
      });
      showToast("Quiz Battle started successfully!");
      setSelectedOpponent("");
    } catch (error) {
      console.error("Error starting battle:", error);
      showToast("Failed to start battle.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlayBattle = (battle) => {
    const allQuestions = sampleQuizQuestions || [];
    const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
    const selectedTen = shuffled.slice(0, 10);

    setShuffledQuestions(selectedTen);
    setActiveBattle(battle);
    setCurrentQIndex(0);
    setScore(0);
    setTimeLeft(60); 
    setIsFinished(false);
  };

  const handleAnswerSubmit = () => {
    const questions = shuffledQuestions.length > 0 ? shuffledQuestions : (sampleQuizQuestions?.slice(0, 10) || []);
    const currentQuestion = questions[currentQIndex];
    if (!currentQuestion) return;

    let updatedScore = score;
    if (selectedOption === currentQuestion.answer) {
      updatedScore += 1;
      setScore(updatedScore);
    }

    setSelectedOption("");
    if (currentQIndex + 1 < questions.length) {
      setCurrentQIndex(prev => prev + 1);
    } else {
      handleFinishBattle(updatedScore);
    }
  };

  const handleFinishBattle = async (finalScore) => {
    setIsFinished(true);
    if (!activeBattle || !currentUser?.uid) return;

    try {
      const battleRef = doc(db, "battles", activeBattle.id);
      const existingScores = activeBattle.scores || {};
      const updatedScores = { ...existingScores, [currentUser.uid]: finalScore };

      const allPlayed = activeBattle.participants.every(
        pId => updatedScores[pId] !== undefined && updatedScores[pId] !== null
      );

      const newStatus = allPlayed ? "completed" : "pending";

      await updateDoc(battleRef, {
        [`scores.${currentUser.uid}`]: finalScore,
        status: newStatus
      });
      
      showToast("Battle score submitted successfully!");
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  const shareToWhatsApp = (opponentUsername) => {
    const myScore = activeBattle?.scores?.[currentUser?.uid] ?? 0;
    const opponentScore = opponentUsername ? (activeBattle?.scores?.[getOpponentDetails(activeBattle.participants)?.id] ?? 0) : 0;
    
    const text = `🔥 I just crushed an epic Quiz Battle against @${opponentUsername} on Agnitutor!\n\n📊 My Score: ${myScore}/10\n🎯 @${opponentUsername}'s Score: ${opponentScore}/10\n\nThink you can beat us? Join the arena now: https://agnitutor.com/`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
  };

  const downloadBadgeImage = () => {
    setIsDownloading(true);
    try {
      const canvasElement = document.createElement("canvas");
      canvasElement.width = 800;
      canvasElement.height = 600;
      const ctx = canvasElement.getContext("2d");

      // Super modern subtle warm gradient background with light orange accents
      const bgGradient = ctx.createLinearGradient(0, 0, 800, 600);
      bgGradient.addColorStop(0, "#fff7ed");
      bgGradient.addColorStop(0.5, "#ffffff");
      bgGradient.addColorStop(1, "#ffedd5");
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvasElement.width, canvasElement.height);

      // Decorative ambient background glow circles
      ctx.save();
      ctx.fillStyle = "rgba(249, 115, 22, 0.07)";
      ctx.beginPath();
      ctx.arc(720, 80, 190, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "rgba(79, 70, 229, 0.04)";
      ctx.beginPath();
      ctx.arc(80, 520, 210, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Main Card Container with refined shadow
      ctx.save();
      ctx.shadowColor = "rgba(234, 88, 12, 0.10)";
      ctx.shadowBlur = 32;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 12;

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.roundRect(40, 40, 720, 520, 24);
      ctx.fill();
      ctx.restore();

      ctx.strokeStyle = "#fed7aa";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(40, 40, 720, 520, 24);
      ctx.stroke();

      const opponent = getOpponentDetails(activeBattle?.participants);

      const drawBadgeContent = (context) => {
        // Top Header badge decoration bar (Vibrant Orange)
        context.fillStyle = "#f97316";
        context.beginPath();
        context.roundRect(80, 72, 6, 28, 3);
        context.fill();

        // Header title
        context.fillStyle = "#1e293b";
        context.font = "800 18px sans-serif";
        context.fillText("AGNITUTOR ARENA BADGE", 98, 91);

        context.fillStyle = "#94a3b8";
        context.font = "600 12px sans-serif";
        context.fillText("OFFICIAL QUIZ DUEL SUMMARY", 515, 91);

        // Divider line
        context.strokeStyle = "#f1f5f9";
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(80, 122);
        context.lineTo(720, 122);
        context.stroke();

        context.fillStyle = "#0f172a";
        context.font = "bold 20px sans-serif";
        context.fillText("📊 Final Battle Summary", 80, 162);

        const myUserProf = getUserProfile(currentUser?.uid);
        const myScore = activeBattle?.scores?.[currentUser?.uid] ?? 0;
        const opponentScore = opponent ? (activeBattle?.scores?.[opponent.id] ?? 0) : 0;
        const opponentName = "Opponent";

        // ==================== BOX 1: OPPONENT (Icon & Clean Neutral Theme) ====================
        context.fillStyle = "#f8fafc";
        context.strokeStyle = "#e2e8f0";
        context.lineWidth = 1;
        context.beginPath();
        context.roundRect(80, 192, 266, 172, 16);
        context.fill();
        context.stroke();

        // Opponent Icon badge circle
        context.fillStyle = "#e2e8f0";
        context.beginPath();
        context.arc(122, 234, 18, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = "#64748b";
        context.font = "16px sans-serif";
        context.fillText("👤", 113, 240);

        context.fillStyle = "#64748b";
        context.font = "700 12px sans-serif";
        context.fillText(opponentName.toUpperCase(), 152, 238);

        context.fillStyle = "#1e293b";
        context.font = "800 36px sans-serif";
        context.fillText(`${opponentScore}`, 108, 316);

        context.fillStyle = "#94a3b8";
        context.font = "600 15px sans-serif";
        context.fillText("/ 10pts", 168, 316);


        // ==================== BOX 2: USER (Avatar Photo & Light Orange Theme) ====================
        context.fillStyle = "#fff7ed";
        context.strokeStyle = "#fed7aa";
        context.lineWidth = 1.5;
        context.beginPath();
        context.roundRect(374, 192, 346, 172, 16);
        context.fill();
        context.stroke();

        // Draw User Avatar Image or Fallback Icon
        const drawUserContent = () => {
          context.fillStyle = "#ea580c";
          context.font = "700 12px sans-serif";
          context.fillText(`@${myUserProf.username} (YOU)`.toUpperCase(), 458, 238);

          context.fillStyle = "#c2410c";
          context.font = "800 36px sans-serif";
          context.fillText(`${myScore}`, 402, 316);

          context.fillStyle = "#fb923c";
          context.font = "600 15px sans-serif";
          context.fillText("/ 10pts", 462, 316);
        };

        if (currentUser?.photoURL) {
          const userAvatar = new Image();
          userAvatar.crossOrigin = "anonymous";
          userAvatar.src = currentUser.photoURL;
          userAvatar.onload = () => {
            context.save();
            context.beginPath();
            context.arc(422, 234, 18, 0, Math.PI * 2);
            context.closePath();
            context.clip();
            context.drawImage(userAvatar, 404, 216, 36, 36);
            context.restore();

            // Avatar border
            context.strokeStyle = "#fb923c";
            context.lineWidth = 1.5;
            context.beginPath();
            context.arc(422, 234, 18, 0, Math.PI * 2);
            context.stroke();

            drawUserContent();
            finalizeBadge();
          };
          userAvatar.onerror = () => {
            // Fallback avatar icon box
            context.fillStyle = "#ffedd5";
            context.beginPath();
            context.arc(422, 234, 18, 0, Math.PI * 2);
            context.fill();
            context.fillStyle = "#ea580c";
            context.font = "16px sans-serif";
            context.fillText("⭐", 413, 240);
            drawUserContent();
            finalizeBadge();
          };
        } else {
          context.fillStyle = "#ffedd5";
          context.beginPath();
          context.arc(422, 234, 18, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = "#ea580c";
          context.font = "16px sans-serif";
          context.fillText("⭐", 413, 240);
          drawUserContent();
          finalizeBadge();
        }
      };

      const finalizeBadge = () => {
        // Result banner
        const isWin = (activeBattle?.scores?.[currentUser?.uid] ?? 0) > (opponent ? (activeBattle?.scores?.[opponent.id] ?? 0) : 0);
        const isLoss = (activeBattle?.scores?.[currentUser?.uid] ?? 0) < (opponent ? (activeBattle?.scores?.[opponent.id] ?? 0) : 0);

        ctx.fillStyle = isWin ? "#ecfdf5" : isLoss ? "#fff7ed" : "#fefce8";
        ctx.strokeStyle = isWin ? "#a7f3d0" : isLoss ? "#fed7aa" : "#fde047";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(80, 386, 640, 68, 14);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = isWin ? "#065f46" : isLoss ? "#9a3412" : "#854d0e";
        ctx.font = "bold 15px sans-serif";
        const resultText = isWin ? "🏆 Victory! You Won the Battle on Agnitutor!" : isLoss ? "💪 Great effort! Keep practicing on Agnitutor." : "🤝 It's a Dead Heat Tie!";
        ctx.fillText(resultText, 110, 426);

        // Footer branding & badge metadata
        ctx.fillStyle = "#94a3b8";
        ctx.font = "bold 11px sans-serif";
        ctx.fillText("POWERED BY AGNITUTOR LEARNING ECOSYSTEM  •  HTTPS://AGNITUTOR.COM", 80, 506);

        ctx.fillStyle = "#cbd5e1";
        ctx.font = "600 11px sans-serif";
        ctx.fillText("SECURE BADGE ID: AGNI-" + Math.floor(100000 + Math.random() * 900000), 540, 506);

        // Trigger download
        const link = document.createElement("a");
        link.download = "Agnitutor-Quiz-Badge.png";
        link.href = canvasElement.toDataURL("image/png");
        link.click();

        setIsDownloading(false);
        showToast("Agnitutor Badge downloaded successfully!");
      };

      // Load and draw Agnitutor Logo
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.src = "/logo.png";

      logoImg.onload = () => {
        ctx.drawImage(logoImg, 38, 68, 32, 32);
        drawBadgeContent(ctx);
      };

      logoImg.onerror = () => {
        drawBadgeContent(ctx);
      };

    } catch (err) {
      console.error("Canvas generation error:", err);
      setIsDownloading(false);
      showToast("Failed to download badge.", "error");
    }
  };
  if (activeBattle) {
    const opponent = getOpponentDetails(activeBattle.participants);
    const questions = shuffledQuestions.length > 0 ? shuffledQuestions : (sampleQuizQuestions?.slice(0, 10) || []);
    const currentQuestion = questions[currentQIndex];
    const myScore = activeBattle.scores?.[currentUser?.uid];
    const opponentScore = opponent ? activeBattle.scores?.[opponent.id] : undefined;
    const isCompleted = activeBattle.status === "completed";
    const hasAlreadyPlayed = myScore !== undefined && myScore !== null;
    const opponentUsername = opponent?.username ? `@${opponent.username}` : "Opponent";

    return (
      <div style={{ background: "#ffffff", borderRadius: "20px", padding: "32px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9", maxWidth: "650px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", borderBottom: "1px solid #f1f5f9", paddingBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#1e293b", display: "flex", alignItems: "center", gap: "10px" }}>
            <span>⚔️</span> Arena vs. {opponentUsername}
          </h3>
          {!hasAlreadyPlayed && !isFinished && (
            <div style={{ background: timeLeft < 15 ? "#fee2e2" : "#eef2ff", color: timeLeft < 15 ? "#dc2626" : "#4f46e5", padding: "8px 16px", borderRadius: "20px", fontSize: "14px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
              <span>⏳</span> {timeLeft}s
            </div>
          )}
        </div>

        {!hasAlreadyPlayed && !isFinished ? (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Question {currentQIndex + 1} of {questions.length}
              </span>
              <div style={{ width: "150px", height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                <div style={{ width: `${((currentQIndex + 1) / questions.length) * 100}%`, height: "100%", background: "#4f46e5", transition: "width 0.3s ease" }}></div>
              </div>
            </div>

            <h4 style={{ fontSize: "18px", fontWeight: "600", color: "#0f172a", marginBottom: "24px", lineHeight: "1.6", textAlign: "left" }}>
              {currentQuestion?.question}
            </h4>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "28px" }}>
              {currentQuestion?.options?.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedOption(opt)}
                  style={{
                    textAlign: "left",
                    padding: "16px 20px",
                    borderRadius: "14px",
                    border: selectedOption === opt ? "2px solid #4f46e5" : "1px solid #e2e8f0",
                    background: selectedOption === opt ? "#eef2ff" : "#ffffff",
                    color: selectedOption === opt ? "#4338ca" : "#334155",
                    fontWeight: selectedOption === opt ? "600" : "400",
                    fontSize: "15px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: selectedOption === opt ? "0 4px 12px rgba(79, 70, 229, 0.12)" : "none"
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>

            <button 
              disabled={!selectedOption}
              onClick={handleAnswerSubmit}
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: "14px",
                border: "none",
                background: !selectedOption ? "#cbd5e1" : "#4f46e5",
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: "600",
                cursor: !selectedOption ? "not-allowed" : "pointer",
                transition: "background 0.2s, transform 0.1s"
              }}
            >
              {currentQIndex + 1 === questions.length ? "Submit Final Answers" : "Next Question →"}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div 
              style={{ 
                background: "#ffffff", 
                padding: "24px", 
                borderRadius: "16px", 
                display: "inline-block", 
                width: "100%", 
                maxWidth: "520px",
                boxSizing: "border-box",
                border: "1px solid #e2e8f0",
                textAlign: "center"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "2px dashed #e2e8f0", paddingBottom: "12px" }}>
                <span style={{ fontSize: "13px", fontWeight: "800", color: "#4f46e5", letterSpacing: "1px", textTransform: "uppercase" }}>
                  🚀 AGNITUTOR ARENA
                </span>
                <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Quiz Duel</span>
              </div>

              <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b", marginBottom: "8px" }}>
                {isCompleted ? "📊 Final Battle Summary" : "⏳ Submitted Successfully!"}
              </h2>
              <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "24px", maxWidth: "420px", margin: "0 auto 24px", lineHeight: "1.5" }}>
                {isCompleted 
                  ? "Both combatants have locked in their scores. Verified by Agnitutor." 
                  : "Your answers have been recorded. Results will unlock instantly once your opponent completes their match."}
              </p>

              <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "18px", borderRadius: "14px", minWidth: "180px", textAlign: "center", flex: 1 }}>
                  <img src={opponent?.photoURL || "https://via.placeholder.com/150"} alt="" style={{ width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", marginBottom: "8px", border: "2px solid #ffffff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} />
                  <div style={{ fontWeight: "700", fontSize: "14px", color: "#1e293b" }}>{opponentUsername}</div>
                  <div style={{ fontSize: "22px", color: "#4f46e5", marginTop: "6px", fontWeight: "700" }}>
                    {opponentScore !== undefined && opponentScore !== null ? `${opponentScore} / 10` : "Pending..."}
                  </div>
                </div>

                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "18px", borderRadius: "14px", minWidth: "180px", textAlign: "center", flex: 1 }}>
                  <img src={currentUser?.photoURL || "https://via.placeholder.com/150"} alt="" style={{ width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", marginBottom: "8px", border: "2px solid #ffffff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} />
                  <div style={{ fontWeight: "700", fontSize: "14px", color: "#1e293b" }}>@{getUserProfile(currentUser?.uid).username} (You)</div>
                  <div style={{ fontSize: "22px", color: "#4f46e5", marginTop: "6px", fontWeight: "700" }}>
                    {myScore !== undefined && myScore !== null ? `${myScore} / 10` : "Pending..."}
                  </div>
                </div>
              </div>

              {isCompleted && (
                <div style={{ 
                  padding: "16px", 
                  background: myScore > opponentScore ? "#ecfdf5" : myScore < opponentScore ? "#fff1f2" : "#fef9c3", 
                  border: `1px solid ${myScore > opponentScore ? "#a7f3d0" : myScore < opponentScore ? "#fecdd3" : "#fef08a"}`,
                  borderRadius: "14px", 
                  marginBottom: "20px",
                  textAlign: "center"
                }}>
                  <span style={{ fontSize: "26px" }}>{myScore > opponentScore ? "🏆" : myScore < opponentScore ? "💪" : "🤝"}</span>
                  <h4 style={{ color: myScore > opponentScore ? "#065f46" : myScore < opponentScore ? "#9f1239" : "#854d0e", margin: "6px 0 2px", fontSize: "16px", fontWeight: "700" }}>
                    {myScore > opponentScore 
                      ? "Victory! You Won the Battle! 🎉" 
                      : myScore < opponentScore 
                      ? "Keep sharpening your skills! 🎯" 
                      : "It's a Dead Heat Tie! 🤝"}
                  </h4>
                </div>
              )}

              <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                Powered by Agnitutor Learning Ecosystem • agnitutor.com
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap", marginTop: "24px" }}>
              {isCompleted && (
                <>
                  <button 
                    onClick={() => shareToWhatsApp(opponent?.username || "opponent")}
                    style={{ background: "#25D366", color: "#ffffff", border: "none", padding: "14px 22px", borderRadius: "12px", fontWeight: "600", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)" }}
                  >
                    💬 Share on WhatsApp
                  </button>
                  <button 
                    onClick={downloadBadgeImage}
                    disabled={isDownloading}
                    style={{ background: "#4f46e5", color: "#ffffff", border: "none", padding: "14px 22px", borderRadius: "12px", fontWeight: "600", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)" }}
                  >
                    📥 {isDownloading ? "Generating Badge..." : "Download Badge"}
                  </button>
                </>
              )}
              <button 
                onClick={() => setActiveBattle(null)} 
                style={{ background: "#475569", color: "#ffffff", border: "none", padding: "14px 22px", borderRadius: "12px", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
              >
                ← Back to Battles
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div style={{ background: "#ffffff", borderRadius: "20px", padding: "28px", boxShadow: "0 8px 25px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9", marginBottom: "24px", textAlign: "center" }}>
        <h3 style={{ margin: "0 0 16px 0", fontSize: "20px", fontWeight: "700", color: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
          <span>⚔️</span> Start a New Quiz Battle
        </h3>
        {eligibleOpponents.length === 0 ? (
          <div style={{ padding: "24px", background: "#f8fafc", borderRadius: "14px" }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>You need followers or connections to start a battle.</p>
          </div>
        ) : (
          <form onSubmit={handleStartBattle} style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <select 
              style={{ flex: "1", maxWidth: "350px", minWidth: "240px", padding: "14px 18px", borderRadius: "12px", border: "1px solid #cbd5e1", background: "#f8fafc", color: "#1e293b", fontSize: "14px", outline: "none" }}
              value={selectedOpponent}
              onChange={(e) => setSelectedOpponent(e.target.value)}
            >
              <option value="">Select a connected peer...</option>
              {eligibleOpponents.map(peer => (
                <option key={peer.id} value={peer.id}>
                  @{peer.username || peer.name || peer.displayName}
                </option>
              ))}
            </select>
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{ background: "#4f46e5", color: "#ffffff", border: "none", padding: "14px 24px", borderRadius: "12px", fontWeight: "600", fontSize: "14px", cursor: "pointer", transition: "background 0.2s", boxShadow: "0 4px 12px rgba(79, 70, 229, 0.2)" }}
            >
              {isSubmitting ? "Starting..." : "⚔️ Challenge Peer"}
            </button>
          </form>
        )}
      </div>

      <div style={{ background: "#ffffff", borderRadius: "20px", padding: "28px", boxShadow: "0 8px 25px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9" }}>
        <h3 style={{ margin: "0 0 20px 0", fontSize: "20px", fontWeight: "700", color: "#1e293b", textAlign: "center" }}>Your Quiz Battles</h3>
        {loadingBattles ? (
          <p style={{ color: "#64748b", fontSize: "14px", textAlign: "center" }}>Loading battles...</p>
        ) : battles.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", background: "#f8fafc", borderRadius: "14px" }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "15px" }}>No active quiz battles yet. Challenge a friend above to begin!</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {battles.map(battle => {
              const myScore = battle.scores?.[currentUser?.uid];
              const hasPlayed = myScore !== undefined && myScore !== null;
              const isCompleted = battle.status === "completed";

              let btnText = "▶️ Play Match";
              let btnBg = "#4f46e5";
              if (isCompleted) {
                btnText = "🏆 Summary";
                btnBg = "#059669";
              } else if (hasPlayed) {
                btnText = "⏳ Status";
                btnBg = "#64748b";
              }

              return (
                <div key={battle.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", padding: "18px 24px", background: "#f8fafc", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "#e0e7ff", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>⚔️</div>
                    <div>
                      <div style={{ fontWeight: "700", fontSize: "16px", color: "#1e293b", textAlign: "left" }}>Agni Arena Duel</div>
                      <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px", fontWeight: "500", textAlign: "left" }}>
                        {isCompleted ? "✨ Match Finished" : hasPlayed ? "⏳ Waiting for Opponent" : "🔥 Your Turn to Play!"}
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handlePlayBattle(battle)}
                    style={{ padding: "12px 22px", fontSize: "14px", fontWeight: "600", borderRadius: "10px", border: "none", background: btnBg, color: "#ffffff", cursor: "pointer", transition: "opacity 0.2s", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
                  >
                    {btnText}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}